import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Upload, FileText, ShieldAlert, CheckCircle2, AlertCircle, ArrowRight, ArrowLeft,
} from "lucide-react";
import { getDocRequirements } from "@/lib/api";
import { authHeaders } from "@/lib/api/_shared";
import { formatInSiteTz, siteTimeBadge, placeTimeLabel } from "@/lib/datetime";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Step 2 of the new-booking funnel.
 * Two uploads: primary doc (PO / Manifest / Invoice) and DG Manifest.
 * Each upload triggers Doc-AI; results are merged into a pre-fill payload
 * passed to /bookings/new/edit via location.state.
 */
export default function NewBookingDocs() {
  const location = useLocation();
  const navigate = useNavigate();
  const slot = location.state;

  // Guard: refresh / direct nav drops state → kick back to step 1.
  useEffect(() => {
    if (!slot || !slot.site || !slot.bay) {
      toast.error("No slot selected — start from step 1.");
      navigate("/bookings/new", { replace: true });
    }
  }, [slot, navigate]);

  const [docReq, setDocReq] = useState(null);
  useEffect(() => {
    if (!slot?.clientId) return;
    (async () => {
      const { data } = await getDocRequirements({ clientId: slot.clientId, siteId: slot.site.id });
      setDocReq(data || { require_primary_doc: false, require_dg_manifest_if_dg: true, require_docs_for_direction: "both" });
    })();
  }, [slot?.clientId, slot?.site?.id]);

  const [primary, setPrimary] = useState({ file: null, parsing: false, parsed: null, err: null });
  const [dg, setDg]           = useState({ file: null, parsing: false, parsed: null, err: null });
  const primaryRef = useRef();
  const dgRef = useRef();

  const upload = async (kind, file) => {
    if (!file) return;
    const setter = kind === "primary" ? setPrimary : setDg;
    const docType = kind === "primary" ? "primary_doc" : "dg_manifest";
    setter({ file, parsing: true, parsed: null, err: null });
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("document_type", docType);
      const r = await fetch(`${BACKEND}/api/parse-document`, { method: "POST", body: fd, headers: await authHeaders() });
      // Content-Type guard — same pattern as DocAIPanel. If prod ingress
      // ever regresses to serving the SPA index for /api/parse-document,
      // the user gets a clean message instead of a "<!DOCTYPE" crash.
      const ct = r.headers.get("content-type") || "";
      if (!ct.includes("application/json")) {
        throw new Error("Doc-AI is temporarily unreachable. Please try again or fill the form manually.");
      }
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.error || data?.detail || `Parse failed (${r.status})`);
      setter({ file, parsing: false, parsed: data, err: null });
      toast.success(`${kind === "primary" ? "Primary document" : "DG manifest"} parsed`);
    } catch (e) {
      setter({ file, parsing: false, parsed: null, err: e.message });
      toast.error(`Doc-AI: ${e.message}`);
    }
  };

  const dgIndicatedByPrimary =
    primary?.parsed?.extracted?.dangerous_goods === true ||
    String(primary?.parsed?.extracted?.dg_un_number || "").trim() !== "";

  const requirePrimary  = !!docReq?.require_primary_doc;
  const requireDg       = !!docReq?.require_dg_manifest_if_dg && dgIndicatedByPrimary;
  const blockingPrimary = requirePrimary && !primary?.parsed && !primary?.file;
  const blockingDg      = requireDg && !dg?.parsed && !dg?.file;
  const canContinue     = !blockingPrimary && !blockingDg;

  const onContinue = () => {
    const prefill = mergeExtractedToPrefill({
      primary: primary?.parsed?.extracted,
      dg: dg?.parsed?.extracted,
    });
    navigate("/bookings/new/edit", {
      state: { ...slot, prefill, docs: { primary: primary?.file?.name, dg: dg?.file?.name } },
    });
  };

  const onSkip = () => {
    navigate("/bookings/new/edit", { state: { ...slot, prefill: {}, docs: {} } });
  };

  if (!slot) return null;

  return (
    <div className="space-y-4 max-w-4xl" data-testid="new-booking-docs">
      <div>
        <div className="text-xs text-slate-500 uppercase tracking-wide">Step 2 of 3</div>
        <h1 className="text-2xl font-semibold tracking-tight">Upload documents</h1>
        <p className="text-sm text-slate-500">
          We&apos;ll use Doc-AI to read these and pre-fill the booking form.
          PO / Manifest / Invoice are accepted in the same upload.
        </p>
      </div>

      <Card>
        <CardContent className="py-4 text-sm flex flex-wrap gap-x-6 gap-y-2">
          <span><strong>Site:</strong> {slot.site.name} ({slot.site.code})</span>
          <span><strong>Bay:</strong> {slot.bay.name}{slot.bay.description ? ` — ${slot.bay.description}` : ""}</span>
          <span><strong>Start:</strong> {formatInSiteTz(slot.scheduledStart, slot.timezone)}</span>
          <span><strong>End:</strong> {formatInSiteTz(slot.scheduledEnd, slot.timezone)}</span>
          <span className="text-xs text-slate-500 bg-slate-100 rounded px-1.5 py-0.5" data-testid="docs-tz-badge">
            {slot.site?.name ? `@ ${placeTimeLabel({ ...slot.site, timezone: slot.timezone }, slot.scheduledStart)}` : siteTimeBadge(slot.timezone)}
          </span>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <UploadCard
          title="Upload PO / Manifest / Invoice"
          subtitle={requirePrimary ? "Required" : "Optional"}
          required={requirePrimary}
          icon={FileText}
          state={primary}
          onPick={() => primaryRef.current?.click()}
          testid="upload-primary"
        />
        <input
          ref={primaryRef}
          type="file"
          accept="application/pdf,image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => upload("primary", e.target.files?.[0])}
          data-testid="primary-file-input"
        />

        <UploadCard
          title="Upload DG Manifest"
          subtitle={requireDg ? "Required (DG indicated)" : "Optional — only if Dangerous Goods"}
          required={requireDg}
          icon={ShieldAlert}
          state={dg}
          onPick={() => dgRef.current?.click()}
          testid="upload-dg"
        />
        <input
          ref={dgRef}
          type="file"
          accept="application/pdf,image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => upload("dg", e.target.files?.[0])}
          data-testid="dg-file-input"
        />
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <Button variant="ghost" onClick={() => navigate(-1)} data-testid="docs-back">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to slot picker
        </Button>
        <div className="flex items-center gap-2">
          {!requirePrimary && !requireDg && (
            <Button variant="outline" onClick={onSkip} data-testid="docs-skip">
              Skip — enter manually
            </Button>
          )}
          <Button onClick={onContinue} disabled={!canContinue} data-testid="docs-continue">
            Continue to booking form <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function UploadCard({ title, subtitle, required, icon: Icon, state, onPick, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="py-5 space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2"><Icon className="h-4 w-4 text-slate-500" /> <span className="font-medium">{title}</span></div>
            <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>
          </div>
          {required && <Badge className="bg-rose-100 text-rose-700">Required</Badge>}
        </div>

        {!state?.file && (
          <Button variant="outline" onClick={onPick} className="w-full">
            <Upload className="h-4 w-4 mr-1.5" /> Choose file
          </Button>
        )}

        {state?.file && state?.parsing && (
          <div className="text-xs text-slate-500">
            <span className="animate-pulse">Parsing {state.file.name}…</span>
          </div>
        )}

        {state?.parsed && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4" /> Parsed: <strong>{state.file.name}</strong> · confidence: {state.parsed.extracted?.confidence || "—"}
          </div>
        )}

        {state?.err && (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 flex items-center gap-1.5">
            <AlertCircle className="h-4 w-4" /> {state.err}
            <Button variant="ghost" size="sm" className="ml-auto" onClick={onPick}>Retry</Button>
          </div>
        )}

        {state?.parsed && (
          <Button variant="ghost" size="sm" onClick={onPick} className="text-xs text-slate-500">
            Replace file
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

/** Merge Doc-AI extracts into a flat prefill object the BookingForm can consume. */
function mergeExtractedToPrefill({ primary, dg }) {
  const p = primary || {};
  const d = dg || {};
  return {
    // Schedule
    title:            p.po_number || p.manifest_number || p.invoice_number || "",
    customer_reference: p.customer_reference || p.po_number || "",
    sender_name:      p.sender_name || "",
    sender_company:   p.sender_company || "",
    receiver_name:    p.receiver_name || "",
    receiver_company: p.receiver_company || "",
    po_number:        p.po_number || "",
    manifest_number:  p.manifest_number || d.manifest_number || "",
    // Goods
    goods_description: p.goods_description || "",
    goods_category:   p.goods_category || (d.dangerous_goods ? "dangerous_goods" : ""),
    pallet_type:      p.pallet_type || "",
    pallets_in:       p.pallets_in || 0,
    pallets_out:      p.pallets_out || 0,
    temperature_min:  p.temperature_min ?? null,
    temperature_max:  p.temperature_max ?? null,
    dangerous_goods:  !!(p.dangerous_goods || d.dangerous_goods || d.dg_un_number),
    dg_un_number:     p.dg_un_number || d.dg_un_number || "",
    dg_class:         p.dg_class || d.dg_class || "",
    dg_packing_group: p.dg_packing_group || d.dg_packing_group || "",
    // Vehicle
    vehicle_type:     p.vehicle_type || "",
    vehicle_rego:     p.vehicle_rego || "",
    // Driver
    driver_name:      p.driver_name || "",
    driver_phone:     p.driver_phone || "",
  };
}
